import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";

import { Profile } from "src/services/User Management";
import { addTitleOption, createOptions } from "../../utils/select";

const service = new Profile();

const REDUCER_MODULE_NAME = "PROFILE";

const initialState = {
  grid: {
    activos: {
      page: 1,
      skip: 0,
      take: 3,
      rows: 0,
      data: [],
      show: 3,
    },
    inactivos: {
      page: 1,
      skip: 0,
      take: 3,
      rows: 0,
      data: [],
      show: 3,
    },
  },
  loading: false,
  formIsReady: false,
  data: "",
  message: "",
  error: "",
  select: [],
};

export const grid_activos = createAsyncThunk(
  `${REDUCER_MODULE_NAME}/grid_activos`,
  async ({ skip, take, currentPage = 1 }) => {
    const { data } = await service.grid(skip, take, 1);

    return {
      skip,
      take,
      grid_data: data,
      page: currentPage,
    };
  },
);

export const grid_inactivos = createAsyncThunk(
  `${REDUCER_MODULE_NAME}/grid_inactivos`,
  async ({ skip, take, currentPage = 1 }) => {
    const { data } = await service.grid(skip, take, 0);

    return {
      skip,
      take,
      grid_data: data,
      page: currentPage,
    };
  },
);

export const getById = createAsyncThunk(
  `${REDUCER_MODULE_NAME}/getById`,
  async (id) => {
    const { data } = await service.getById(id);

    return {
      entity: data,
    };
  },
);

export const select = createAsyncThunk(
  `${REDUCER_MODULE_NAME}/select`,
  async () => {
    const { data } = await service.select();

    return {
      profiles: addTitleOption(createOptions(data)),
    };
  },
);

export const changeActive = createAsyncThunk(
  `${REDUCER_MODULE_NAME}/changeActive`,
  async ({ id }, { dispatch, getState }) => {
    await service.changeActive(id);

    const { profile } = getState();

    dispatch(
      grid_activos({
        skip: profile.grid.activos.skip,
        take: profile.grid.activos.take,
        currentPage: profile.grid.activos.page,
      }),
    );
    dispatch(
      grid_inactivos({
        skip: profile.grid.inactivos.skip,
        take: profile.grid.inactivos.take,
        currentPage: profile.grid.inactivos.page,
      }),
    );
  },
);

export const form = createAsyncThunk(
  `${REDUCER_MODULE_NAME}/form`,
  async (id, { dispatch }) => {
    if (id) {
      dispatch(getById(id));
    }
  },
);

export const submit = createAsyncThunk(
  `${REDUCER_MODULE_NAME}/submit`,
  async ({ id, data }, { rejectWithValue }) => {
    try {
      if (id) {
        await service.update(id, data);
      } else {
        await service.create(data);
      }

      return {
        message: `Perfil ${id ? "modificado" : "creado"} correctamente.`,
      };
    } catch (error) {
      return rejectWithValue(error.errors || error.message);
    }
  },
);

export const updatePermissions = createAsyncThunk(
  `${REDUCER_MODULE_NAME}/updatePermissions`,
  async ({ id, permissions }, { rejectWithValue }) => {
    try {
      await service.updatePermissions(id, permissions);

      return {
        message: "Permisos actualizados correctamente.",
      };
    } catch (error) {
      return rejectWithValue(error.errors || error.message);
    }
  },
);

export const profileSlice = createSlice({
  name: REDUCER_MODULE_NAME,
  initialState,
  reducers: {
    clean: () => initialState,
    cleanMessageAndError: (state) => {
      state.message = "";
      state.error = "";
      state.formIsReady = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(grid_activos.fulfilled, (state, action) => {
        const { page, skip, take, grid_data } = action.payload;
        state.grid.activos = {
          ...state.grid.activos,
          page,
          skip,
          take,
          rows: grid_data.rows,
          data: grid_data.data,
        };
      })
      .addCase(grid_inactivos.fulfilled, (state, action) => {
        const { page, skip, take, grid_data } = action.payload;
        state.grid.inactivos = {
          ...state.grid.inactivos,
          page,
          skip,
          take,
          rows: grid_data.rows,
          data: grid_data.data,
        };
      })
      .addCase(getById.fulfilled, (state, action) => {
        state.data = action.payload.entity || "";
      })
      .addCase(select.fulfilled, (state, action) => {
        state.select = action.payload.profiles;
      })
      .addCase(submit.fulfilled, (state, action) => {
        state.message = action.payload.message;
        state.formIsReady = true;
      })
      .addCase(updatePermissions.fulfilled, (state, action) => {
        state.message = action.payload.message;
        state.formIsReady = true;
      })

      // Global Matchers
      .addMatcher(
        isAnyOf(
          grid_activos.pending,
          grid_inactivos.pending,
          changeActive.pending,
          getById.pending,
          select.pending,
          form.pending,
          submit.pending,
          updatePermissions.pending,
        ),
        (state) => {
          state.loading = true;
          state.error = "";
          state.formIsReady = false;
        },
      )
      .addMatcher(
        isAnyOf(
          grid_activos.fulfilled,
          grid_inactivos.fulfilled,
          changeActive.fulfilled,
          getById.fulfilled,
          select.fulfilled,
          form.fulfilled,
          submit.fulfilled,
          updatePermissions.fulfilled,
        ),
        (state) => {
          state.loading = false;
        },
      )
      .addMatcher(
        isAnyOf(
          grid_activos.rejected,
          grid_inactivos.rejected,
          changeActive.rejected,
          getById.rejected,
          select.rejected,
          submit.rejected,
          updatePermissions.rejected,
        ),
        (state, action) => {
          state.loading = false;
          state.error = action.payload || "Error desconocido";
        },
      );
  },
});

export const { clean, cleanMessageAndError } = profileSlice.actions;

export default profileSlice.reducer;
